import { getDb } from '../db';
import { DEFAULT_SETTINGS } from '../seed/defaults';
import type { Settings } from '../../core/types';

const KEY = 'settings' as const;

/**
 * Settings are a singleton row. Reads merge over `DEFAULT_SETTINGS` so a field
 * added in a later version is never `undefined` at a call site — the app must boot
 * with a complete configuration even if the stored row predates half of it.
 */
export const settingsRepo = {
  async get(): Promise<Settings> {
    const db = await getDb();
    const stored = await db.get('settings', KEY);
    return { ...DEFAULT_SETTINGS, ...stored };
  },

  async update(patch: Partial<Settings>): Promise<Settings> {
    const db = await getDb();
    const next: Settings = { ...(await this.get()), ...patch };
    await db.put('settings', next, KEY);
    return next;
  },

  /** Wholesale replace — used by import, which must not merge with stale values. */
  async replace(settings: Settings): Promise<void> {
    const db = await getDb();
    await db.put('settings', settings, KEY);
  },

  async reset(): Promise<Settings> {
    await this.replace(DEFAULT_SETTINGS);
    return DEFAULT_SETTINGS;
  },
};
